import { Label } from "@progress/kendo-react-labels";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { useMemo } from "react";
import {
  currentStageSections,
  disabledSections,
  entryValues,
  formErrors,
  selectedTab,
  template,
  templateSections,
  templateStageSteps,
} from "../../Entry/Manage/EditEntrySignals";
import { AdaptiveField } from "../../Fields/FieldTypes/FieldFactory";
import { Invisible } from "../../Utilities/EntryUtils";

export const Section = ({ isPrintPreview = false, handleSubmit }) => {
  function renderField(field, sectionIndex, fieldIndex, isTabField, isStageField, isPrintPreview) {
    if (field.fieldType === "Index") return;

    const props = {
      field: field,
      fieldId: field?.fieldId,
      fieldName: field?.fieldName,
      fieldType: field?.fieldType,
      sectionIndex: sectionIndex,
      fieldIndex: fieldIndex,
      value: field?.value || "",
      handleFieldChange: handleFieldChange,
      handleSubmit: handleSubmit,
      labelVisible: true,
      isTabField: isTabField,
      isStageField: isStageField,
      isPrintPreview: isPrintPreview,
    };

    return <AdaptiveField {...props} />;
  }

  const updateDisabledSections = (sectionName, isDisabled) => {
    const currentDisabledSections = { ...disabledSections.value };

    if (currentDisabledSections[sectionName] !== isDisabled) {
      currentDisabledSections[sectionName] = isDisabled;
      disabledSections.value = currentDisabledSections;
    }
  };

  const clearInvisibleSectionFields = ({ section, sectionIndex }) => {
    section.fields.forEach((field) => {
      if (field.value) {
        if (field.fieldType.includes("Picker")) {
          let newTemplate = JSON.tryParse(JSON.stringify(template.value));

          if (newTemplate.sections[sectionIndex]) {
            let fieldValue = "";
            let fieldIndex = newTemplate.sections[sectionIndex].fields.findIndex(
              (targetField) => targetField.fieldName === field.fieldName
            );

            newTemplate.sections[sectionIndex].fields[fieldIndex].value = fieldValue;

            entryValues.value = {
              ...entryValues.value,
              [field.fieldName]: fieldValue,
            };

            template.value = newTemplate;
          }
        } else if (field.fieldType === "Checkbox") {
          field.value = "false";
        } else {
          field.value = "";
        }
      }
    });
  };

  const buildSectionStyles = (section, isDisabled) => {
    const labelStyle = {
      fontSize: (section.properties?.fontSize || "21") + "px",
      fontWeight: section.properties?.fontWeight || "bold",
      margin:
        (section.properties?.marginTop || 0) +
        "px " +
        (section.properties?.marginRight || 0) +
        "px " +
        (section.properties?.marginBottom || 0) +
        "px " +
        (section.properties?.marginLeft || 0) +
        "px ",
    };

    if (isDisabled) {
      labelStyle.color = "gray";
    }

    const sectionStyle = {
      padding:
        (section.properties?.paddingTop || 0) +
        "px " +
        (section.properties?.paddingRight || 0) +
        "px " +
        (section.properties?.paddingBottom || 0) +
        "px " +
        (section.properties?.paddingLeft || 0) +
        "px ",
    };

    return { labelStyle, sectionStyle };
  };

  const renderSection = (section, sectionIndex, isPrintPreview) => {
    if (templateSections.value) {
      sectionIndex = templateSections.value.findIndex(
        (_section) => JSON.stringify(section) === JSON.stringify(_section)
      );

      const visibilityResult = Invisible({
        VisibilityRules: section.properties?.VisibilityRules,
        parameterValues: entryValues.value,
      });

      let isInvisible = visibilityResult.invisible && !visibilityResult.disabled;
      let isDisabled = visibilityResult.invisible && visibilityResult.disabled;

      if (isInvisible) {
        clearInvisibleSectionFields({ section, sectionIndex });
        return null;
      }

      updateDisabledSections(section.sectionName, isDisabled);

      const fields = section.fields?.map((field, fieldIndex) =>
        renderField(
          field,
          sectionIndex,
          fieldIndex,
          isPrintPreview ? false : section.properties?.IsTab,
          isPrintPreview ? false : templateStageSteps.value?.length > 0,
          isPrintPreview
        )
      );

      const { labelStyle, sectionStyle } = buildSectionStyles(section, isDisabled);

      const sectionContent = (
        <div style={sectionStyle}>
          <Label
            className="k-d-block"
            style={labelStyle}
            key={section.sectionName + "_" + sectionIndex}
          >
            {section.sectionName}
          </Label>
          <div
            key={
              section.properties.StageStep?.text + "_" + section.sectionName + "_" + sectionIndex
            }
            className="fields-wrapper"
          >
            {fields}
          </div>
        </div>
      );

      if (section.properties?.IsTab && !isPrintPreview) {
        return (
          <TabStripTab
            contentClassName={`${
              formErrors.value?.[section.sectionName]?.length > 0 ? "k-border-red " : ""
            }`}
            title={
              <span
                style={{
                  color: formErrors.value?.[section.sectionName]?.length > 0 ? "#a80000 " : "",
                }}
              >
                {section.sectionName}
              </span>
            }
            key={section.sectionName + "_" + sectionIndex}
            disabled={isDisabled}
          >
            <div
              key={
                section.properties.StageStep?.text + "_" + section.sectionName + "_" + sectionIndex
              }
              className="fields-wrapper"
            >
              {fields}
            </div>
          </TabStripTab>
        );
      }
      return sectionContent;
    }
  };

  const tabSections = useMemo(
    () => templateSections.value?.filter((section) => section.properties?.IsTab) || [],
    [templateSections.value]
  );

  const getDisabledTabColors = (color) => {
    if (!color || color.trim() === "") return "#e0e0e0";

    let r = 200,
      g = 200,
      b = 200,
      a = 1;

    // Hex color (#RGB or #RRGGBB)
    if (color.startsWith("#")) {
      const hex = color.substring(1);
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      }
    }
    // RGB or RGBA color
    else if (color.startsWith("rgb")) {
      const parts = color.match(/(\d+)/g);
      if (parts) {
        r = parseInt(parts[0]);
        g = parseInt(parts[1]);
        b = parseInt(parts[2]);
        a = parts[3] ? parseFloat(parts[3]) : 1;
      }
    }

    const grayValue = Math.round((r + g + b) / 3);

    const lightGrayFactor = 0.7;
    const desaturateFactor = 0.6;

    const newR = Math.round(
      grayValue * desaturateFactor + r * (1 - desaturateFactor) * lightGrayFactor
    );
    const newG = Math.round(
      grayValue * desaturateFactor + g * (1 - desaturateFactor) * lightGrayFactor
    );
    const newB = Math.round(
      grayValue * desaturateFactor + b * (1 - desaturateFactor) * lightGrayFactor
    );

    if (color.includes("rgba")) {
      return `rgba(${newR}, ${newG}, ${newB}, ${a})`;
    }
    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  const dynamicTabStyles = useMemo(() => {
    const visibleTabSections = tabSections.filter((section) => {
      const visibilityResult = Invisible({
        VisibilityRules: section.properties?.VisibilityRules,
        parameterValues: entryValues.value,
      });
      return !(visibilityResult.invisible && !visibilityResult.disabled);
    });

    const customColorStyles = visibleTabSections
      .map((section, renderIndex) => {
        const tabColor = section.properties?.TabColor ?? "#FFFFFF";
        const tabTextColor = section.properties?.TabTextColor ?? "#000000";

        const normalColor = getDisabledTabColors(tabColor);
        const activeColor = tabColor;

        const normalTextColor = getDisabledTabColors(tabTextColor);
        const activeTextColor = tabTextColor;

        return `
        .custom-colored-tabstrip .k-tabstrip-items .k-item:nth-child(${
          renderIndex + 1
        }):not(.k-active) {
          background-color: ${normalColor} !important;
          color: ${normalTextColor} !important;
          border-color: ${normalColor} !important;
        }
        
        .custom-colored-tabstrip .k-tabstrip-items .k-item:nth-child(${
          renderIndex + 1
        }):not(.k-active):hover {
          background-color: ${tabColor} !important;
          color: ${tabTextColor} !important;
        }
        
        .custom-colored-tabstrip .k-tabstrip-items .k-item:nth-child(${renderIndex + 1}).k-active {
          background-color: ${activeColor} !important;
          color: ${activeTextColor} !important;
          border-bottom-color: transparent !important;
        }
      `;
      })
      .join("");

    return customColorStyles;
  }, [tabSections, entryValues.value]);

  const handleFieldChange = ({ e, sectionIndex, fieldIndex }) => {
    const newTemplate = JSON.tryParse(JSON.stringify(template.value));
    const fieldValue = e?.value?.toString() ?? "";
    newTemplate.sections[sectionIndex].fields[fieldIndex].value = fieldValue;
    const field = newTemplate.sections[sectionIndex].fields[fieldIndex];

    if (!e.unmounted) {
      entryValues.value = {
        ...entryValues.value,
        [field.fieldName]: fieldValue,
      };
    }

    template.value = newTemplate;
  };

  if (isPrintPreview) {
    return (
      <div>
        {templateSections.value?.map((section, sectionIndex) =>
          renderSection(section, sectionIndex, isPrintPreview)
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Staged sections render */}
      {currentStageSections.value?.length > 0
        ? currentStageSections.value?.map((section, sectionIndex) =>
            renderSection(section, sectionIndex, isPrintPreview)
          )
        : templateStageSteps.value?.length > 0 && (
            <p className="k-text-center k-mt-3">No sections assigned to this stage</p>
          )}

      {/* Regular sections render */}
      {!templateStageSteps.value?.length > 0 &&
        templateSections.value?.map((section, sectionIndex) =>
          renderSection(section, sectionIndex, isPrintPreview)
        )}

      {/* Tabs w/ stages render */}
      {templateSections.value?.some((section) => section.properties?.IsTab) &&
        templateStageSteps.value?.length > 0 && (
          <>
            <style>{dynamicTabStyles}</style>
            <TabStrip
              className="k-mt-5 !k-fs-xl !k-font-weight-bold k-overflow-hidden custom-colored-tabstrip"
              selected={selectedTab.value}
              onSelect={(e) => (selectedTab.value = e.selected)}
              tabContentStyle={{
                borderBottomRightRadius: "10px",
                borderBottomLeftRadius: "10px",
                borderTopRightRadius: "10px",
                borderTopLeftRadius: selectedTab.value > 0 ? "10px" : "0px",
              }}
            >
              {currentStageSections.value
                ?.filter((section) => section.properties?.IsTab)
                .map((section, sectionIndex) =>
                  renderSection(section, sectionIndex, isPrintPreview)
                )}
            </TabStrip>
          </>
        )}

      {/* Regular tabs render */}
      {templateSections.value?.some((section) => section.properties?.IsTab) &&
        !templateStageSteps.value?.length > 0 && (
          <>
            <style>{dynamicTabStyles}</style>
            <TabStrip
              className="k-mt-5 !k-fs-xl !k-font-weight-bold k-overflow-hidden custom-colored-tabstrip"
              selected={selectedTab.value}
              onSelect={(e) => (selectedTab.value = e.selected)}
              tabContentStyle={{
                borderBottomRightRadius: "10px",
                borderBottomLeftRadius: "10px",
                borderTopRightRadius: "10px",
                borderTopLeftRadius: selectedTab.value > 0 ? "10px" : "0px",
              }}
            >
              {templateSections.value
                ?.filter((section) => section.properties?.IsTab)
                .map((section, sectionIndex) =>
                  renderSection(section, sectionIndex, isPrintPreview)
                )}
            </TabStrip>
          </>
        )}
    </div>
  );
};
